import { useEffect } from 'react';
import { disconnectAll } from '../graphBuilder';
import { stopAllElements } from '../helpers/stopAllElements';
import type { EngineRefs } from '../refs';
import type { ScheduledStem } from '../../bufferTransport';

export function useGlobalSafety(
  refs: EngineRefs,
  opts: {
    keepPlayingInBackground: boolean;
    bufferModeRef: React.MutableRefObject<boolean>;
    scheduledRef: React.MutableRefObject<Record<string, ScheduledStem> | null>;
    pauseAll: () => void;
    stopAll: (sched: Record<string, ScheduledStem>) => void;
    setStatus: (s: 'idle' | 'loading' | 'playing' | 'paused') => void;
    setCurrentTime: (t: number) => void;
  }
) {
  useEffect(() => {
    const stopAudioNow = () => {
  try { opts.pauseAll(); } catch {}
  try { stopAllElements(refs.elsRef.current || {}); } catch {}
      try { if (opts.bufferModeRef.current && opts.scheduledRef.current) { opts.stopAll(opts.scheduledRef.current); } } catch {}
      try { const ctx = refs.audioCtxRef.current; if (ctx && ctx.state !== 'closed') { ctx.suspend().catch(()=>{}); } } catch {}
      opts.setStatus('idle'); opts.setCurrentTime(0);
      // disconnect graph nodes deterministically
      try {
        const stems = Object.keys(refs.gainsRef.current).map(role => ({
          role,
          src: refs.sourcesRef.current[role] ?? null,
          delay: refs.delaysRef.current[role],
          gain: refs.gainsRef.current[role],
          analyser: refs.analyzersRef.current[role],
        }));
        const master = refs.masterGainRef.current && refs.masterFilterRef.current && refs.masterHighpassRef.current
          ? {
              masterGain: refs.masterGainRef.current!,
              hp: refs.masterHighpassRef.current!,
              lp: refs.masterFilterRef.current!,
              analyser: refs.masterAnalyserRef.current ?? null,
            }
          : null;
        disconnectAll(master as any, stems as any);
      } catch {}
      // clear refs
      try {
        refs.sourcesRef.current = {} as any;
        refs.gainsRef.current = {} as any;
        refs.delaysRef.current = {} as any;
        refs.analyzersRef.current = {} as any;
        refs.connectedElsRef.current = {} as any;
        refs.masterAnalyserRef.current = null;
        refs.masterFilterRef.current = null;
        refs.masterHighpassRef.current = null;
        refs.masterGainRef.current = null;
      } catch {}
    };

    const onPageHide = () => { if (!opts.keepPlayingInBackground) stopAudioNow(); };
    const routeChange = () => stopAudioNow();
    const hist = window.history as any;
    const origPush = hist.pushState?.bind(window.history);
    const origReplace = hist.replaceState?.bind(window.history);
    if (origPush) {
      hist.pushState = function (...args: any[]) { const ret = origPush(...args); try { routeChange(); } catch {} return ret; };
    }
    if (origReplace) {
      hist.replaceState = function (...args: any[]) { const ret = origReplace(...args); try { routeChange(); } catch {} return ret; };
    }
    window.addEventListener('popstate', routeChange);
    window.addEventListener('hashchange', routeChange);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('popstate', routeChange);
      window.removeEventListener('hashchange', routeChange);
      if (origPush) { try { window.history.pushState = origPush; } catch {} }
      if (origReplace) { try { window.history.replaceState = origReplace; } catch {} }
      // full cleanup on unmount; also close context if we own it
      stopAudioNow();
      try { const ctx = refs.audioCtxRef.current; if (ctx && ctx.state !== 'closed') { ctx.close().catch(()=>{}); } } catch {}
    };
  }, []);
}
